import { Formik } from 'formik';
import {
  ActorRole,
  AssessmentExpiryOptionUnit,
  AssessmentStageKind,
  CognitiveMode,
  ProjectPageGetProjectByIdDocument,
  ProjectTeamEmailMode,
  UpdateAssessmentStageMutationVariables,
  useCreateStageMutation,
  useProjectPageGetProjectByIdQuery,
  useUpdateAssessmentStageMutation,
} from 'generated/graphql';
import { FC, useState } from 'react';
import { TestletsAdjustmentNotificationModal } from 'views/Project/components/Stages/StageForm/TestletsAdjustmentNotificationModal';
import {
  Button,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
  MIN_TEST_QUESTIONS,
  parseActorRolesForSelect,
  stageKindLabel,
} from '../helpers';
import StageHeader from '../StageHeader/StageHeader';
import { convertToUTC, createValidationSchema } from './StageForm.helper';
import { IStageFormProps, IStageFormValues } from './StageForm.interfaces';
import styles from './StageForm.module.scss';
import StageFormFields from './StageFormFields';

const StageForm: FC<IStageFormProps> = ({
  onSubmit,
  onCancel,
  projectId,
  initialValues,
  stageId,
  companyExpiryOption,
  cognitiveSkillsVersion,
  hasRoleOnlyOneCognitiveSkill,
  companyHideReports,
}) => {
  const { handleMsgType } = useNotification();
  const [
    testletAdjustmentNotificationModalData,
    setTestletAdjustmentNotificationModalData,
  ] = useState<UpdateAssessmentStageMutationVariables | null>(null);

  const { data: projectData } = useProjectPageGetProjectByIdQuery({
    variables: {
      id: projectId,
    },
  });
  const productSolution = projectData?.project?.productSolution;
  const assessmentStage = stageId
    ? projectData?.project?.assessmentStagesV2.find(s => s.id === stageId) ??
      null
    : null;

  const projectRoles = productSolution
    ? parseActorRolesForSelect(productSolution)
    : [];

  const validationSchema = createValidationSchema(projectRoles.length);

  const [createStage, { loading: createLoading }] = useCreateStageMutation({
    refetchQueries: [
      { query: ProjectPageGetProjectByIdDocument, variables: { id: projectId } },
    ],
    onCompleted: () => {
      handleMsgType({
        type: TNotification.success,
        title: 'Successfully created assessment stage',
        dismissable: true,
      });
      onSubmit();
    },
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        dismissable: true,
        title: 'Failed to create assessment stage',
        message: error?.message || 'Something went wrong. Please try again.',
      });
    },
  });

  const [editStage, { loading: editLoading }] =
    useUpdateAssessmentStageMutation({
      onError: error => {
        handleMsgType({ type: TNotification.error, message: error?.message });
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'Successfully updated assessment stage',
          dismissable: true,
        });
        onSubmit();
      },
      refetchQueries: [
        { query: ProjectPageGetProjectByIdDocument, variables: { id: projectId } },
      ],
    });

  const openTestletAdjustmentNotificationModal = (
    data: UpdateAssessmentStageMutationVariables
  ) => {
    setTestletAdjustmentNotificationModalData(data);
  };

  const closeTestletAdjustmentNotificationModal = () => {
    setTestletAdjustmentNotificationModalData(null);
  };

  const handleTestletAdjustmentNotificationModalContinue = () => {
    if (testletAdjustmentNotificationModalData) {
      editStage({
        variables: testletAdjustmentNotificationModalData,
      });
      closeTestletAdjustmentNotificationModal();
    }
  };

  // Get cognitive tests from the assessmentStage for validation
  const cognitiveTests = assessmentStage?.actions?.cognitiveTests;

  // If user changes from "Streamline" cognitive mode to "In depth",
  // and if the stage in has some test with number of questions per trait that is below
  // the minimal number of question per trait for cognitive mode, on backend side the number of
  // questions will be updated. With this function we will check if that is the case.
  const numberOfTestQuestionsPerTraitValidation = (
    newCognitiveMode: CognitiveMode
  ) => {
    let valid = true;
    if (newCognitiveMode !== CognitiveMode.InDepth || !cognitiveTests) {
      return valid;
    }

    cognitiveTests.forEach(test => {
      if (!test.sectionCount) return;

      const minQuestions = MIN_TEST_QUESTIONS[CognitiveMode.InDepth][test.kind];

      if (test.sectionCount < minQuestions) {
        valid = false;
      }
    });

    return valid;
  };

  const handleSaveForm = (formValues: IStageFormValues) => {
    const basePayload = {
      name: formValues.stageName,
      startTime: convertToUTC(formValues.stageStartTime),
      endTime:
        !formValues.stageIsEndTimeNull && formValues.stageEndTime
          ? convertToUTC(formValues.stageEndTime, true)
          : null,
      cognitiveMode: formValues.cognitiveMode,
      assessmentExpiryOption:
        formValues.stageAssessmentExpiry.value === 'none'
          ? null
          : {
            duration: Number(formValues.stageAssessmentExpiry.value),
            unit: AssessmentExpiryOptionUnit.Day,
          },
      managerReportId: formValues.managerReport?.value
        ? Number(formValues.managerReport.value)
        : null,
      candidateReportId: formValues.candidateReport?.value
        ? Number(formValues.candidateReport.value)
        : null,
    };

    if (stageId) {
      const updateInput = {
        ...basePayload,
        emailSettings: {
          emailCandidateReport: formValues.stageEmailCandidateReport,
          managerReportId: formValues.managerReport?.value
            ? Number(formValues.managerReport.value)
            : null,
          candidateReportId: formValues.candidateReport?.value
            ? Number(formValues.candidateReport.value)
            : null,
          projectTeamEmailSettings: {
            mode:
              formValues.stageMode.value || ProjectTeamEmailMode.AccessBased,
            actorRoles:
              formValues.stageProjectRoles?.map(
                role => role.value as ActorRole
              ) || [],
          },
        },
      };

      const isValidNumberOfTestQuestionsPerTrait =
        numberOfTestQuestionsPerTraitValidation(formValues.cognitiveMode);

      // If cognitive skills version is v1, then we don't need to open the testlet adjustment notification modal because testlets are blocked from being adjusted (both BE and FE)
      if (
        !isValidNumberOfTestQuestionsPerTrait &&
        cognitiveSkillsVersion !== 'v1'
      ) {
        openTestletAdjustmentNotificationModal({
          id: stageId,
          input: updateInput,
        });
        return;
      }

      editStage({
        variables: {
          id: stageId,
          input: updateInput,
        },
      });
      return;
    }

    // Create stage (still uses old mutation for now)
    createStage({
      variables: {
        projectId,
        name: formValues.stageName,
        startTime: convertToUTC(formValues.stageStartTime),
        endTime:
          !formValues.stageIsEndTimeNull && formValues.stageEndTime
            ? convertToUTC(formValues.stageEndTime, true)
            : null,
        emailCandidateReport: formValues.stageEmailCandidateReport,
        hasCalibration: false,
        cognitiveMode: formValues.cognitiveMode,
        managerReportId: formValues.managerReport?.value
          ? Number(formValues.managerReport.value)
          : null,
        candidateReportId: formValues.candidateReport?.value
          ? Number(formValues.candidateReport.value)
          : null,
        projectTeamEmailSettings: {
          mode: formValues.stageMode.value || ProjectTeamEmailMode.AccessBased,
          actorRoles:
            formValues.stageProjectRoles?.map(
              role => role.value as ActorRole
            ) || [],
        },
        kind: formValues.stageKind.value as AssessmentStageKind,
      },
    });
  };

  return (
    <>
      <Formik<IStageFormValues>
        initialValues={initialValues}
        onSubmit={values => handleSaveForm(values)}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ handleSubmit }) => {
          return (
            <div className={styles.container}>
              <StageHeader
                title={`${stageKindLabel(
                  initialValues.stageKind.value ?? AssessmentStageKind.Assessment
                )} stage`}
                primaryButton={
                  <Button
                    onClick={() => handleSubmit()}
                    disabled={createLoading || editLoading}
                    loading={createLoading || editLoading}
                    size="medium"
                    data-testid="stage-header__primary-button-save"
                  >
                    {createLoading || editLoading ? 'Saving...' : 'Save'}
                  </Button>
                }
                secondaryButton={
                  <Button
                    onClick={onCancel}
                    variant="secondary"
                    size="medium"
                    data-testid="stage-header__secondary-button-cancel"
                  >
                    Cancel
                  </Button>
                }
              />
              <StageFormFields
                projectRoles={projectRoles}
                companyExpiryOption={companyExpiryOption || null}
                cognitiveSkillsVersion={cognitiveSkillsVersion}
                hasRoleOnlyOneCognitiveSkill={hasRoleOnlyOneCognitiveSkill}
                companyHideReports={companyHideReports}
              />
            </div>
          );
        }}
      </Formik>
      {Boolean(testletAdjustmentNotificationModalData) && (
        <TestletsAdjustmentNotificationModal
          onCancel={closeTestletAdjustmentNotificationModal}
          onContinue={handleTestletAdjustmentNotificationModalContinue}
        />
      )}
    </>
  );
};

export default StageForm;
